// Dérivation pure « qui peut quoi » sur la page réunion (page-meeting.ts).
//
// Extraite du render (audit dette 2026-07-02 §3.3) pour être testable sans
// DOM : toutes les décisions d'affichage conditionnées par le rôle
// (animateur/participant), le cycle de vie (ouverte/fermée), le mode Solo et
// les permissions de palier sont calculées ICI, en un seul endroit.
// Complète computeVisibleTabs (domain/meeting-form.ts) qui porte la matrice
// des onglets.
//
// Convention : AUCUN accès à window/DOM ici — le module reçoit un prédicat
// `can(permission)` (deny-by-default : si le helper de perms n'est pas
// chargé, l'appelant passe un prédicat qui renvoie false).

export interface MeetingRoleSource {
  animators: { id: number }[];
  participants: { id: number }[];
}

export interface MeetingRoles {
  isAnimator: boolean;
  isParticipant: boolean;
}

/** Rôles de l'utilisateur courant sur la réunion (aucun rôle en création). */
export function deriveMeetingRoles(
  meeting: MeetingRoleSource | null,
  uid: number | null | undefined,
): MeetingRoles {
  const isAnimator = !!(meeting && uid && meeting.animators.some((a) => a.id === uid));
  const isParticipant = !!(meeting && uid && meeting.participants.some((p) => p.id === uid));
  return { isAnimator, isParticipant };
}

export interface MeetingCapabilityContext {
  /** false en création (pas encore de réunion enregistrée). */
  hasMeeting: boolean;
  isAnimator: boolean;
  isParticipant: boolean;
  /** opened_at posé (réunion ouverte). */
  isOpen: boolean;
  /** closed_at posé (réunion fermée, tout en lecture seule). */
  isClosed: boolean;
  /** Permission globale meetings_records.write. */
  canWrite: boolean;
  /** Mode mono-utilisateur (US) : verrous « solo » sur les blocs collaboratifs. */
  isSolo: boolean;
  /** Prédicat de permission (deny-by-default si helper absent). */
  can: (permission: string) => boolean;
}

export interface MeetingCapabilities {
  /** Création : droit global requis. Édition : être animateur suffit
   * (cohérent backend — rattacher un brouillon calendrier à un comité). */
  canSaveMeeting: boolean;
  /** Bloc Informations en lecture seule (édition par non-animateur). */
  infoReadOnly: boolean;
  /** Champs du bloc Informations désactivés (readonly OU création sans droit). */
  infoDisabled: boolean;
  /** Clé i18n du CTA bas-droite : « Fermer » pour un pur participant en
   * consultation tant que la réunion n'est pas ouverte, sinon « Annuler ». */
  cancelLabelKey: 'common.close' | 'common.cancel';
  /** CTA « Fermer la réunion » : animateur, tant que non fermée. */
  showCloseBar: boolean;
  /** Onglet Indicateurs : permission meeting_indicators.read (palier). */
  indicatorsAllowed: boolean;
  /** Génération IA du CR : permission meetings.analyze (palier). */
  canAnalyze: boolean;
  /** Verrous « solo » (onglet participants, RACI, bouton proposer). */
  lockSolo: boolean;
}

export function deriveMeetingCapabilities(ctx: MeetingCapabilityContext): MeetingCapabilities {
  const infoReadOnly = ctx.hasMeeting && !ctx.isAnimator;
  return {
    canSaveMeeting: ctx.hasMeeting ? ctx.isAnimator : ctx.canWrite,
    infoReadOnly,
    infoDisabled: ctx.hasMeeting ? infoReadOnly : !ctx.canWrite,
    cancelLabelKey:
      ctx.hasMeeting && ctx.isParticipant && !ctx.isAnimator && !ctx.isOpen
        ? 'common.close'
        : 'common.cancel',
    showCloseBar: ctx.isAnimator && !ctx.isClosed,
    indicatorsAllowed: ctx.can('meeting_indicators.read'),
    canAnalyze: ctx.can('meetings.analyze'),
    lockSolo: ctx.isSolo,
  };
}
